import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useState } from "react";
import { Menu, X, Zap, User, LogOut, LayoutDashboard, BookOpen, Trophy, FolderOpen, Heart } from "lucide-react";

export default function Header() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const navLinks = user
    ? [
        { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
        { to: "/interests", label: "Interests", icon: Heart },
        { to: "/blog", label: "Content", icon: BookOpen },
        { to: "/leaderboard", label: "Leaderboard", icon: Trophy },
        { to: "/resources", label: "Resources", icon: FolderOpen },
      ]
    : [];

  const isActive = (path) => location.pathname === path;

  return (
    <header data-testid="main-header" className="fixed top-0 w-full z-50 bg-[#0A0A0C]/80 backdrop-blur-xl border-b border-[#2A2A30]">
      <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
        <Link to={user ? "/dashboard" : "/"} className="flex items-center gap-2 group" data-testid="header-logo">
          <div className="w-8 h-8 bg-[#E31937] flex items-center justify-center">
            <Zap className="w-5 h-5 text-white" />
          </div>
          <span className="font-bold text-lg text-white tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
            CTCSB
          </span>
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-1">
          {navLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              data-testid={`nav-${link.label.toLowerCase()}`}
              className={`px-4 py-2 text-sm font-medium transition-colors ${
                isActive(link.to)
                  ? "text-[#E31937] bg-[#E31937]/10"
                  : "text-slate-400 hover:text-white hover:bg-white/5"
              }`}
            >
              {link.label}
            </Link>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          {user ? (
            <>
              <div className="flex items-center gap-2 text-sm text-slate-400">
                <User className="w-4 h-4" />
                <span>{user.name || user.email}</span>
              </div>
              <button
                onClick={handleLogout}
                data-testid="logout-button"
                className="flex items-center gap-1 px-4 py-2 text-sm text-slate-400 hover:text-white transition-colors"
              >
                <LogOut className="w-4 h-4" />
                Logout
              </button>
            </>
          ) : (
            <>
              <Link
                to="/login"
                data-testid="header-login-btn"
                className="px-4 py-2 text-sm font-medium text-slate-300 hover:text-white transition-colors"
              >
                Log In
              </Link>
              <Link
                to="/register"
                data-testid="header-register-btn"
                className="px-6 py-2 text-sm font-bold uppercase tracking-wide bg-[#E31937] text-white hover:bg-[#FF3B30] transition-colors"
              >
                Join Now
              </Link>
            </>
          )}
        </div>

        {/* Mobile Toggle */}
        <button
          className="md:hidden text-white"
          onClick={() => setMobileOpen(!mobileOpen)}
          data-testid="mobile-menu-toggle"
        >
          {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile Nav */}
      {mobileOpen && (
        <div className="md:hidden bg-[#0A0A0C] border-t border-[#2A2A30] px-6 py-4 space-y-2">
          {navLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              onClick={() => setMobileOpen(false)}
              className={`block px-4 py-3 text-sm font-medium transition-colors ${
                isActive(link.to) ? "text-[#E31937] bg-[#E31937]/10" : "text-slate-400 hover:text-white"
              }`}
            >
              <link.icon className="w-4 h-4 inline mr-2" />
              {link.label}
            </Link>
          ))}
          {user ? (
            <button
              onClick={() => { handleLogout(); setMobileOpen(false); }}
              className="w-full text-left px-4 py-3 text-sm text-slate-400 hover:text-white"
            >
              <LogOut className="w-4 h-4 inline mr-2" />
              Logout
            </button>
          ) : (
            <div className="space-y-2 pt-2">
              <Link to="/login" onClick={() => setMobileOpen(false)} className="block px-4 py-3 text-sm text-slate-300 hover:text-white">Log In</Link>
              <Link to="/register" onClick={() => setMobileOpen(false)} className="block px-4 py-3 text-sm bg-[#E31937] text-white text-center font-bold uppercase">Join Now</Link>
            </div>
          )}
        </div>
      )}
    </header>
  );
}
